import React from 'react';

import { Box, Col, Row, Text } from '@opendoor/bricks-next';
import { IconStarFilled, IconStarHalfFilled } from '@tabler/icons-react';
import { EntryComponent } from 'cms/entries/entries';
import { ICompareReviewQuote, ICompareReviews } from 'declarations/contentful';

const STAR_COLOR = '#E5A100';

const StarRating: React.FC<{ rating: number }> = ({ rating }) => {
  const fullStars = Math.floor(rating);
  const hasHalf = rating - fullStars >= 0.3;
  return (
    <Row gap="$0dot5x" accessibilityRole="image" accessibilityLabel={`${rating} out of 5 stars`}>
      {Array.from({ length: fullStars }, (_, i) => (
        <IconStarFilled key={i} size={14} color={STAR_COLOR} aria-hidden="true" />
      ))}
      {hasHalf && <IconStarHalfFilled size={14} color={STAR_COLOR} aria-hidden="true" />}
    </Row>
  );
};

const ReviewCard: React.FC<{
  review: ICompareReviewQuote;
  variant: 'competitor' | 'opendoor';
}> = ({ review, variant }) => (
  <Col
    p="$12x"
    borderRadius="$6x"
    gap="$6x"
    backgroundColor="$backgroundPrimary"
    borderWidth={1}
    borderColor={variant === 'opendoor' ? '$borderAccentBrand' : '$borderSecondary'}
  >
    {variant === 'opendoor' && review.fields.starRating != null && (
      <StarRating rating={review.fields.starRating} />
    )}
    <Text
      tag="p"
      typography="$bodySmall"
      color={variant === 'opendoor' ? '$contentPrimary' : '$contentSecondary'}
      $platform-web={{ lineHeight: '1.65' }}
    >
      &ldquo;{review.fields.quote}&rdquo;
    </Text>
    <Text
      tag="span"
      typography="$bodyXsmall"
      fontWeight="$medium"
      color={variant === 'opendoor' ? '$contentAccentBrand' : '$contentTertiary'}
    >
      {variant === 'competitor' ? '— ' : ''}
      {review.fields.attribution}
    </Text>
  </Col>
);

const RenderCompareReviews = (entry: ICompareReviews) => {
  const { fields } = entry;

  return (
    <Box backgroundColor="$backgroundSecondary" py="$40x" $smallerThanMD={{ py: '$24x' }}>
      <Col maxWidth={1120} px="$12x" width="100%" mx="auto">
        <Col gap="$12x">
          <Col gap="$6x">
            <Text tag="p" typography="$labelSmall" fontWeight="$medium" color="$contentAccentBrand">
              {fields.eyebrow}
            </Text>
            <Text
              tag="h2"
              typography="$subheaderLarge"
              color="$contentPrimary"
              $smallerThanMD={{ typography: '$subheaderMedium' }}
            >
              {fields.title}
            </Text>
            <Text tag="p" typography="$bodyLarge" color="$contentSecondary" maxWidth={580}>
              {fields.description}
            </Text>
          </Col>

          <Box flexDirection="column" gap="$24x" $largerThanSM={{ flexDirection: 'row' }}>
            {/* Competitor column */}
            <Col flex={1} gap="$8x">
              <Row gap="$6x" alignItems="center">
                <Text tag="span" typography="$labelMedium" color="$contentTertiary">
                  {fields.competitorName}
                </Text>
                <Row gap="$2x" alignItems="center">
                  <StarRating rating={fields.competitorRating} />
                  <Text tag="span" typography="$bodyXsmall" color="$contentTertiary">
                    {fields.competitorRating}
                    {fields.competitorReviewCount ? ` · ${fields.competitorReviewCount}` : ''}
                  </Text>
                </Row>
              </Row>
              <Col gap="$8x">
                {fields.competitorReviews.map((review) => (
                  <ReviewCard key={review.sys.id} review={review} variant="competitor" />
                ))}
              </Col>
            </Col>

            {/* Opendoor column */}
            <Col flex={1} gap="$8x">
              <Row gap="$6x" alignItems="center">
                <Text tag="span" typography="$labelMedium" color="$contentAccentBrand">
                  Opendoor
                </Text>
                <Row gap="$2x" alignItems="center">
                  <StarRating rating={fields.opendoorRating} />
                  <Text tag="span" typography="$bodyXsmall" color="$contentTertiary">
                    {fields.opendoorRating} · {fields.opendoorReviewCount}
                  </Text>
                </Row>
              </Row>
              <Col gap="$8x">
                {fields.opendoorReviews.map((review) => (
                  <ReviewCard key={review.sys.id} review={review} variant="opendoor" />
                ))}
              </Col>
            </Col>
          </Box>
        </Col>
      </Col>
    </Box>
  );
};

const CompareReviews: EntryComponent<ICompareReviews> = {
  render: RenderCompareReviews,
};

export default CompareReviews;
